import { Link, useLocation } from 'react-router-dom'
import { useTelegramBackButton } from '@/hooks/use-telegram-back-button'
import { MessageCircle, LogOut } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth'
import { useClientUiConfig } from '@/hooks/use-client-ui'
import { ICON_MAP, TAB_PATHS, resolveTabsForRole } from '@/lib/client-ui'
import { Button } from '@/components/ui/button'
import { toast } from 'sonner'

interface SimpleLayoutProps {
  children: React.ReactNode
}

export function SimpleLayout({ children }: SimpleLayoutProps) {
  useTelegramBackButton()
  const location = useLocation()
  const pathname = location.pathname
  const { isAuthenticated, logout, user } = useAuthStore()

  const handleLogout = async () => {
    try {
      await logout()
      toast.success('已成功登出')
    } catch (error) {
      toast.error('登出失敗')
    }
  }

  const handleDevLogout = () => {
    localStorage.removeItem('access_token')
    window.location.reload()
    toast.success('開發者模式：已強制登出')
  }

  // 導航由 admin 可編排的 client-ui 配置驅動（見 docs/DESIGN.md 與 /admin/appearance）
  const { config } = useClientUiConfig()
  const tabs = user ? resolveTabsForRole(config, user.role) : []
  const filteredNavigation = tabs.map(tab => ({
    name: tab.label,
    href: TAB_PATHS[tab.key],
    icon: ICON_MAP[tab.icon],
  }))

  // 檢查是否應該隱藏導航欄的頁面
  const hideNavigation = pathname.includes('/teacher/lookbook') || (pathname.includes('/courses/') && pathname.includes('/rednote'))

  return (
    <div className={cn(
      "min-h-screen bg-background",
      !hideNavigation && "pb-14 md:pb-0"
    )}>
      {/* Header（桌面版導航與登出；行動版由底部導航列提供） */}
      {!hideNavigation && (
        <header className="bg-card shadow-sm border-b border-border">
          <div className="max-w-page mx-auto px-4 sm:px-6">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-2">
                <MessageCircle className="w-8 h-8 text-blue-600" />
                <h1 className="text-xl font-semibold text-foreground">ClawedFlare</h1>
              </div>
              
              <div className="flex items-center gap-4">
                {/* Navigation */}
                <nav className="hidden md:flex space-x-6">
                  {filteredNavigation.map((item) => {
                    const isActive = pathname === item.href
                    const Icon = item.icon
                    
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={cn(
                          'flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                          isActive
                            ? 'text-blue-600 bg-blue-600/10'
                            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                        )}
                      >
                        <Icon className="w-4 h-4" />
                        <span>{item.name}</span>
                      </Link>
                    )
                  })}
                </nav>

                {/* User Actions */}
                {isAuthenticated && (
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground hidden md:block">
                      {user?.firstName}
                    </span>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleLogout}
                      className="flex items-center gap-1"
                    >
                      <LogOut className="w-3 h-3" />
                      <span className="hidden sm:inline">登出</span>
                    </Button>
                    {import.meta.env.DEV && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleDevLogout}
                        className="text-orange-600 hover:bg-orange-50 flex items-center gap-1"
                      >
                        <LogOut className="w-3 h-3" />
                        <span className="hidden sm:inline">強制</span>
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className={cn(
        "max-w-page mx-auto px-4 sm:px-6 py-8",
        !hideNavigation && "pb-20 md:pb-8"
      )}>
        {children}
      </main>

      {/* Mobile Tabbar：56px + iOS 安全區，最多 5 個 tab（resolveTabsForRole 已裁切） */}
      {!hideNavigation && filteredNavigation.length > 0 && (
        <nav
          className="md:hidden fixed bottom-0 left-0 right-0 bg-card shadow-border"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className={cn(
            'grid h-14',
            filteredNavigation.length === 2 ? 'grid-cols-2' :
            filteredNavigation.length === 3 ? 'grid-cols-3' :
            filteredNavigation.length === 4 ? 'grid-cols-4' : 'grid-cols-5'
          )}>
            {filteredNavigation.map((item) => {
              const isActive = item.href === '/'
                ? pathname === '/'
                : pathname === item.href || pathname.startsWith(item.href + '/')
              const Icon = item.icon

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex flex-col items-center justify-center gap-0.5 text-xs font-medium transition-colors',
                    isActive ? 'text-foreground' : 'text-muted-foreground'
                  )}
                >
                  <Icon className={cn('w-5 h-5', isActive && 'text-blue-600')} />
                  <span className="text-[10px] leading-none">{item.name}</span>
                </Link>
              )
            })}
          </div>
        </nav>
      )}
    </div>
  )
}